/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { assign, createMachine, Interpreter } from 'xstate-v4'
import {
  LocationSuggestionCampsite,
  LocationSuggestionCustom,
  LocationSuggestionHierarchy,
  LocationSuggestionRecentSearch,
  LocationSuggestions,
} from '../domain/LocationSuggestions'
import { Events } from './events'

interface Context {
  input: string
  langCode: string
  suggestions: LocationSuggestions
}

function getContextInitial() {
  return {
    input: '',
    langCode: 'en-gb',
    suggestions: {},
  }
}

export function returnSuggestionsMachine() {
  return createMachine<Context, Events>(
    {
      id: 'suggestions',
      context: getContextInitial(),
      initial: 'idle',
      on: {
        SET_INPUT: [
          {
            target: 'debouncing',
            cond: (_, evt) => evt.input.length > 0,
            actions: 'assignInput',
          },
          {
            target: 'clear',
          },
        ],
        SET_INITIAL_INPUT: {
          actions: assign({
            input: (_, evt) => evt.input,
            suggestions: {},
          }),
        },
        SELECT_LOCATION: {
          actions: assign({
            input: (_, evt) => (evt.name ? evt.name : ''),
          }),
        },
        LOAD_POINT_FOR_CUSTOM: {
          target: 'loadingCustomGeoPoint',
        },
        LOAD_RECENT_SEARCHES: {
          target: 'loadOnlyRecentSearches',
        },
      },
      states: {
        idle: {},
        debouncing: {
          after: {
            400: 'loading',
          },
        },
        loading: {
          initial: 'loadingCampsiteAndHierarchy',
          states: {
            loadingCampsiteAndHierarchy: {
              type: 'parallel',
              states: {
                hierarchy: {
                  initial: 'loading',
                  states: {
                    loading: {
                      invoke: {
                        src: 'getHierarchySuggestions',
                        onDone: {
                          target: 'done' as const,
                          actions: 'assignHierarchySuggestions',
                        },
                        onError: {
                          target: 'done' as const,
                          actions: ['logError', 'resetHierarchySuggestions'],
                        },
                      },
                    },
                    done: {
                      always: {
                        target:
                          '#suggestions.loading.loadedCampsiteAndHierarchy',
                        cond: 'campsiteAndHierarchySuggestionsLoaded',
                      },
                    },
                  },
                },
                campsite: {
                  initial: 'loading',
                  states: {
                    loading: {
                      invoke: {
                        src: 'getCampsiteSuggestions',
                        onDone: {
                          target: 'done' as const,
                          actions: 'assignCampsiteSuggestions',
                        },
                        onError: {
                          target: 'done' as const,
                          actions: ['logError', 'resetCampsiteSuggestions'],
                        },
                      },
                    },
                    done: {
                      always: {
                        target:
                          '#suggestions.loading.loadedCampsiteAndHierarchy',
                        cond: 'campsiteAndHierarchySuggestionsLoaded',
                      },
                    },
                  },
                },
                recentSearch: {
                  initial: 'loading',
                  states: {
                    loading: {
                      invoke: {
                        src: 'getRecentSearchSuggestions',
                        onDone: {
                          target: 'done' as const,
                          actions: 'assignRecentSearchSuggestions',
                        },
                        onError: {
                          target: 'done' as const,
                          actions: ['logError', 'resetRecentSearchSuggestions'],
                        },
                      },
                    },
                    done: {
                      always: {
                        target:
                          '#suggestions.loading.loadedCampsiteAndHierarchy',
                        cond: 'campsiteAndHierarchySuggestionsLoaded',
                      },
                    },
                  },
                },
              },
            },
            loadedCampsiteAndHierarchy: {
              always: [
                {
                  target: 'loadingCustomGeo',
                  cond: 'emptyHierarchy',
                },
                { target: 'done', actions: 'resetCustomSuggestions' },
              ],
            },
            loadingCustomGeo: {
              invoke: {
                src: 'getCustomGeoSuggestions',
                onDone: {
                  target: 'done',
                  actions: 'assignCustomSuggestions',
                },
                onError: {
                  target: 'done' as const,
                  actions: ['resetCustomSuggestions'],
                },
              },
            },
            done: {
              always: { target: '#suggestions.idle' },
            },
          },
        },
        loadOnlyRecentSearches: {
          initial: 'loading',
          states: {
            loading: {
              invoke: {
                src: 'getRecentSearchSuggestions',
                onDone: {
                  target: 'done' as const,
                  actions: 'assignRecentSearchSuggestions',
                },
                onError: {
                  target: 'done' as const,
                  actions: ['logError', 'resetRecentSearchSuggestions'],
                },
              },
            },
            done: {
              always: {
                target: '#suggestions.idle',
              },
            },
          },
        },
        loadingCustomGeoPoint: {
          invoke: {
            src: 'getPointForCustomGeoSuggestion',
            onDone: {
              target: '#suggestions.idle',
              actions: 'assignCustomSuggestionPoint',
            },
            onError: {
              target: '#suggestions.error',
            },
          },
        },
        clear: {
          entry: ['clearInput'],
          always: 'idle',
        },
        error: {},
      },
    },
    {
      actions: {
        assignHierarchySuggestions: assignSuggestions<{
          data: LocationSuggestionHierarchy[]
        }>(({ data }) => ({ hierarchies: data.length > 0 ? data : undefined })),
        resetHierarchySuggestions: assignSuggestions(() => ({
          hierarchies: undefined,
        })),
        assignCampsiteSuggestions: assignSuggestions<{
          data: LocationSuggestionCampsite[]
        }>(({ data }) => ({ campsites: data.length > 0 ? data : undefined })),
        resetCampsiteSuggestions: assignSuggestions(() => ({
          campsites: undefined,
        })),
        assignRecentSearchSuggestions: assignSuggestions<{
          data: LocationSuggestionRecentSearch[]
        }>(({ data }) => ({
          recentSearches: data.length > 0 ? data : undefined,
        })),
        resetRecentSearchSuggestions: assignSuggestions(() => ({
          recentSearches: undefined,
        })),
        assignCustomSuggestions: assignSuggestions<{
          data: LocationSuggestionCustom[]
        }>(({ data }) => ({ custom: data.length > 0 ? data : undefined })),
        resetCustomSuggestions: assignSuggestions(() => ({
          custom: undefined,
        })),
        assignInput: assign({
          input: (_, evt) =>
            evt.type === 'SET_INPUT' || evt.type === 'SET_INITIAL_INPUT'
              ? evt.input
              : '',
        }),
        assignCustomSuggestionPoint: assign({
          suggestions: ({ suggestions }, evt) => {
            const idx = suggestions.custom?.findIndex(
              (suggestion) =>
                suggestion.mapboxId === evt.data.properties.mapbox_id,
            )

            const coordinates = evt.data.properties.coordinates

            if (suggestions.custom && idx !== undefined && idx > -1) {
              suggestions.custom[idx].point = {
                lat: coordinates.latitude,
                lng: coordinates.longitude,
                isEmptyPoint: false,
              }
            } else {
              console.error(
                'suggestionsMachine: GeoPoint loaded for custom suggestion not found in suggestions',
              )
            }

            return suggestions
          },
        }),
        // @ts-expect-error - XState assign type inference issue with context reset
        clearInput: assign({
          input: () => '',
          suggestions: () => ({}),
          langCode: (ctx) => ctx.langCode || 'en-gb',
        }),
        logError: () => {
          throw new Error('suggestionsMachine logError not implemented')
        },
      },
      guards: {
        emptyHierarchy: (ctx) => !ctx.suggestions.hierarchies,
        campsiteAndHierarchySuggestionsLoaded: (_ctx, _evt, { state }) =>
          state.matches('loading.loadingCampsiteAndHierarchy.hierarchy.done') &&
          state.matches('loading.loadingCampsiteAndHierarchy.campsite.done') &&
          state.matches(
            'loading.loadingCampsiteAndHierarchy.recentSearch.done',
          ),
      },
    },
  )
}

function assignSuggestions<TEventPayload>(
  modificationFn: (evt: TEventPayload) => Partial<Context['suggestions']>,
) {
  return assign<Context, Events>({
    suggestions: ({ suggestions }, evt) => ({
      ...suggestions,
      ...modificationFn(evt as any),
    }),
  })
}

export type SuggestionsMachineInterpret = Interpreter<Context, any, Events>
